import React, { useEffect, useState } from 'react';
import './LoadingScreen.css';

// Durée minimale d'affichage du loader (revue PO) : l'écran ne se ferme
// jamais avant, même si le chargement réel est plus rapide. La barre
// atteint ~85 % sur cette durée puis crawl (⚠️ SYNC avec l'animation
// loading-screen-fill, LoadingScreen.css).
const MIN_DISPLAY_MS = 2200;

interface LoadingScreenProps {
  isLoading: boolean;
  children?: React.ReactNode;
}

export const LoadingScreen: React.FC<LoadingScreenProps> = ({ isLoading, children }) => {
  // Figer la couleur du fond au montage pour éviter un flash si le thème change
  // pendant le loader : fond sombre en night mode, clair en day mode.
  const [background] = useState(() => {
    if (typeof document === 'undefined') return undefined;
    const resolved = document.documentElement.getAttribute('data-theme');
    return resolved === 'day' ? '#FFFFFF' : '#0F0F0F';
  });

  // Durée minimale écoulée : l'écran reste affiché tant que la barre n'a pas
  // fini sa progression, puis suit isLoading (autant que nécessaire).
  const [minDurationDone, setMinDurationDone] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setMinDurationDone(true), MIN_DISPLAY_MS);
    return () => window.clearTimeout(timer);
  }, []);

  // Ne jamais afficher le loader si aucun chargement n'a réellement commencé
  // (GlobalLoading est monté globalement, y compris sur la landing publique :
  // sans ce garde-fou, MIN_DISPLAY_MS imposait 2,2s de loader au refresh).
  const [hasBeenLoading, setHasBeenLoading] = useState(isLoading);
  useEffect(() => {
    if (isLoading) setHasBeenLoading(true);
  }, [isLoading]);

  if (!hasBeenLoading || (!isLoading && minDurationDone)) {
    return <>{children ?? null}</>;
  }

  return (
    <div className="LoadingScreen" style={{ background }} role="status" aria-label="Loading">
      <div className="LoadingScreen__container">
        {/* Lapin qui court GRIS (APNG détouré blanc — même asset que le loader
            de génération du GeneratePanel, grisé par l'opacité dans le CSS),
            sans fond d'icône : le fond de la page porte le thème. En day
            mode le CSS le passe en noir (brightness(0)) avant l'opacité —
            même gris sur fond blanc. alt vide +
            aria-hidden : tant que l'APNG n'est pas téléchargé on n'affiche
            rien (jamais le texte alt) ; l'accessibilité est portée par le
            role="status" du conteneur. Préchargé dans index.html. */}
        <img
          className="LoadingScreen__image"
          src="/rabbit-loader-anim.png"
          alt=""
          aria-hidden="true"
        />
      </div>
      {/* Barre de chargement : progression réelle 0 → 100 % sur
          MIN_DISPLAY_MS (remplie ensuite tant que le chargement continue). */}
      <div className="LoadingScreen__progress" role="progressbar">
        <div className="LoadingScreen__progress-bar" />
      </div>
    </div>
  );
};

export default LoadingScreen;
